import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import DemoBanner from '../components/DemoBanner';
import { useAuth } from '../context/AuthContext';
import VideoTable from '../components/VideoTable';
import FilterSection from '../components/FilterSection';
import BrandImage from '../components/BrandImage';
import { DICT } from '../utils/dictionary';
import { API_BASE } from '../utils/api';

// White Label: Prefixo de Storage dinâmico via .env
const LS_KEY = (user) => `${process.env.REACT_APP_STORAGE_PREFIX || 'app'}_prefs_videos_${(user?.nome || 'anon').toUpperCase()}`;

// Mock Data estabilizado e estruturado para refletir a API real
const MOCK_VIDEOS = [
  {
    id: 101,
    titulo: "Como Escalar Vendas em 2026",
    views: 1542000,
    status: "pago",
    data_publicacao: "2026-02-15",
    equipe: [
      { id: 1, nome: "Maycon Bruno", cargo: DICT.CARGOS_ETAPAS[0] || "Especialista", bonus: 1000.00, status: "pago" },
      { id: 2, nome: "Renata Oliveira", cargo: DICT.CARGOS_ETAPAS[2] || "Designer", bonus: 500.00, status: "pago" }
    ]
  },
  {
    id: 102,
    titulo: "Tutorial: Configuração Avançada de CRM",
    views: 890500,
    status: "pago",
    data_publicacao: "2026-02-20",
    equipe: [
      { id: 3, nome: "Lucas S.", cargo: DICT.CARGOS_ETAPAS[1] || "Analista", bonus: 850.00, status: "pago" }
    ]
  },
  {
    id: 103,
    titulo: "Estratégias de Liderança Remota",
    views: 210000,
    status: "pendente",
    data_publicacao: "2026-03-01",
    equipe: [
      { id: 4, nome: "Mariana Costa", cargo: DICT.CARGOS_ETAPAS[0] || "Especialista", bonus: 0, status: "pendente" }
    ]
  },
  {
    id: 104,
    titulo: "Estudo de Caso: Conversão em Landing Pages",
    views: 450000,
    status: "pendente",
    data_publicacao: "2026-02-28",
    equipe: [] // Vazio de propósito para o cliente testar a atribuição "do zero" na demo
  }
];

const Videos = () => {
  const { user, logout, token, isDemo } = useAuth();
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    views: '',
    titulo: '',
    colaborador: '',
    cargo: '',
    ordem: 'recentes',
    mes: '',
    ano: ''
  });
  const [pageSize, setPageSize] = useState(25);

  // Carregar preferências do localStorage
  useEffect(() => {
    const raw = localStorage.getItem(LS_KEY(user));
    if (raw) {
      try {
        const prefs = JSON.parse(raw);
        if (prefs.filters) setFilters(prev => ({ ...prev, ...prefs.filters }));
        if (prefs.pageSize) setPageSize(prefs.pageSize);
      } catch { /* ignora erro de parse */ }
    }
  }, [user]);

  // Salvar preferências
  useEffect(() => {
    const current = JSON.parse(localStorage.getItem(LS_KEY(user)) || '{}');
    localStorage.setItem(LS_KEY(user), JSON.stringify({ ...current, filters, pageSize }));
  }, [filters, pageSize, user]);

  const saveTablePrefs = useCallback((patch) => {
    if (patch?.pageSize) setPageSize(patch.pageSize);
    const current = JSON.parse(localStorage.getItem(LS_KEY(user)) || '{}');
    localStorage.setItem(LS_KEY(user), JSON.stringify({ ...current, pageSize: patch?.pageSize || pageSize, sort: patch?.sort }));
  }, [user, pageSize]);

  const fetchVideos = useCallback(async () => {
    // LÓGICA DE FILTRAGEM PARA O MODO DEMONSTRAÇÃO
    if (isDemo) {
      let filteredMock = [...MOCK_VIDEOS];

      if (filters.status) {
        filteredMock = filteredMock.filter(v => v.status === filters.status);
      }
      if (filters.views) {
        filteredMock = filteredMock.filter(v => v.views >= Number(filters.views));
      }
      if (filters.titulo) {
        filteredMock = filteredMock.filter(v => v.titulo.toLowerCase().includes(filters.titulo.toLowerCase()));
      }
      if (filters.colaborador) {
        filteredMock = filteredMock.filter(v => v.equipe && v.equipe.some(e => e.nome.toLowerCase().includes(filters.colaborador.toLowerCase())));
      }
      if (filters.cargo) {
        filteredMock = filteredMock.filter(v => v.equipe && v.equipe.some(e => e.cargo.toLowerCase().includes(filters.cargo.toLowerCase())));
      }
      if (filters.mes) {
        filteredMock = filteredMock.filter(v => {
          if (!v.data_publicacao) return false;
          const [, mes] = v.data_publicacao.split('-');
          return mes === filters.mes;
        });
      }
      if (filters.ano) {
        filteredMock = filteredMock.filter(v => {
          if (!v.data_publicacao) return false;
          const [ano] = v.data_publicacao.split('-');
          return ano === String(filters.ano);
        });
      }

      // Ordenação local
      filteredMock.sort((a, b) => {
        if (filters.ordem === 'recentes') return new Date(b.data_publicacao) - new Date(a.data_publicacao);
        if (filters.ordem === 'antigos') return new Date(a.data_publicacao) - new Date(b.data_publicacao);
        if (filters.ordem === 'mais_views') return b.views - a.views;
        if (filters.ordem === 'menos_views') return a.views - b.views;
        return 0;
      });

      setVideos(filteredMock);
      return;
    }

    // LÓGICA DE PRODUÇÃO (Chamada Real à API)
    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${API_BASE}/videos?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) throw new Error(`Erro ao buscar ${DICT.CONTENT_PLUR.toLowerCase()}`);
      const data = await response.json();
      setVideos(Array.isArray(data) ? data : (data.items || []));
    } catch (err) {
      console.error('Erro:', err.message);
      setError(err.message);
      setVideos([]);
    }
  }, [token, filters, isDemo, logout]);

  useEffect(() => {
    if (!user || (!token && !isDemo)) {
      logout();
      return;
    }
    fetchVideos();

    // Polling desativado em modo Demo para economizar CPU
    if (!isDemo) {
      const intervalo = setInterval(fetchVideos, 5 * 60 * 1000);
      return () => clearInterval(intervalo);
    }
  }, [user, token, fetchVideos, isDemo, logout]);

  return (
    <Container fluid className="py-4">
      {isDemo && <DemoBanner />}
      <Row className="align-items-center mb-4">
        <Col xs={12} md={6}>
          <h2 className="fw-bold" style={{ color: 'var(--brand-secondary)' }}>
            <i className="bi bi-camera-reels me-2 text-primary"></i> Banco de {DICT.CONTENT_PLUR}
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end d-none d-md-block">
          <BrandImage 
            envKey="REACT_APP_BANNER_VIDEOS"
            defaultAsset="/client_assets/aero_athos.png" 
            alt="Identidade Visual Vídeos"
            style={{ maxWidth: 150, height: 'auto' }} 
          />
        </Col>
      </Row>

      {error && <Alert variant="danger" className="shadow-sm border-0">{error}</Alert>}

      <div className="card border-0 shadow-sm mb-4 p-3" style={{ backgroundColor: 'var(--brand-bg-light, #fff)' }}>
        <FilterSection filters={filters} setFilters={setFilters} />
      </div>

      <div className="card border-0 shadow-sm overflow-hidden">
        <VideoTable
          videos={videos}
          setVideos={setVideos}
          initialPageSize={pageSize}
          onPrefsChange={saveTablePrefs}
          isDemo={isDemo}
        />
      </div>
    </Container>
  );
};

export default Videos;